/**
 * PlanWidget — dashboard widget showing current plan, usage vs limits, renewal date
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../context/SubscriptionContext';
import { getDateLocale } from '../utils/dateLocale';
import './PlanWidget.css';

function PlanWidget() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { subscription, usage, loading, hasActiveSubscription } = useSubscription();

  if (loading) return null;

  if (!hasActiveSubscription()) {
    return (
      <div className="plan-widget plan-widget--no-plan">
        <div className="plan-widget-header">
          <h3>{t('planWidget.title')}</h3>
        </div>
        <div className="plan-widget-body">
          <p className="plan-widget-no-plan">{t('planWidget.noPlan')}</p>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => navigate('/plan-selection')}
          >
            {t('planWidget.choosePlan')}
          </button>
        </div>
      </div>
    );
  }

  const planId = subscription?.planId || 'free';
  const isTrialing = subscription?.status === 'trial';
  const renewalDate = subscription?.currentPeriodEnd
    ? new Date(subscription.currentPeriodEnd)
    : null;

  const trialEnd = subscription?.trialEnd ? new Date(subscription.trialEnd) : null;
  const trialDaysRemaining =
    isTrialing && trialEnd ? Math.max(0, Math.ceil((trialEnd - new Date()) / 86400000)) : null;

  const usageData = usage?.usage || {};
  const limits = usage?.limits || {};

  const getUsagePercent = (current, max) => {
    if (max === -1 || max === 0) return 0;
    return Math.min(Math.round((current / max) * 100), 100);
  };

  const getBarClass = (pct) => {
    if (pct >= 90) return 'plan-widget-bar--danger';
    if (pct >= 70) return 'plan-widget-bar--warning';
    return 'plan-widget-bar--ok';
  };

  const usageItems = [
    { key: 'menuItems', current: usageData.menuItems || 0, max: limits.maxMenuItems },
    { key: 'tables', current: usageData.tables || 0, max: limits.maxTables },
    { key: 'orders', current: usageData.orders || 0, max: limits.maxMonthlyOrders },
  ].filter((item) => item.max !== undefined);

  return (
    <div className="plan-widget">
      <div className="plan-widget-header">
        <h3>{t('planWidget.title')}</h3>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => navigate('/subscription')}
        >
          {t('planWidget.manage')}
        </button>
      </div>
      <div className="plan-widget-body">
        <div className="plan-widget-plan-row">
          <span className="plan-widget-plan-name">
            {t(`planSelection.plans.${planId}.name`, planId)}
          </span>
          {isTrialing && (
            <span className="plan-widget-trial-badge">
              {t('planWidget.trialDays', { days: trialDaysRemaining })}
            </span>
          )}
        </div>

        {usageItems.length > 0 && (
          <div className="plan-widget-usage">
            {usageItems.map((item) => {
              const pct = getUsagePercent(item.current, item.max);
              const isUnlimited = item.max === -1;
              return (
                <div key={item.key} className="plan-widget-usage-item">
                  <div className="plan-widget-usage-label">
                    <span>{t(`subscription.${item.key === 'orders' ? 'orders' : item.key}`)}</span>
                    <span className="plan-widget-usage-numbers">
                      {item.current}
                      {!isUnlimited ? ` / ${item.max}` : ` / ${t('subscription.unlimited')}`}
                    </span>
                  </div>
                  {!isUnlimited && (
                    <div className="plan-widget-bar">
                      <div
                        className={`plan-widget-bar-fill ${getBarClass(pct)}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {renewalDate && (
          <div className="plan-widget-renewal">
            {t('planWidget.renewsOn', {
              date: renewalDate.toLocaleDateString(getDateLocale(i18n.language), {
                day: 'numeric',
                month: 'short',
              }),
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default PlanWidget;
